// Typed client for the report-writer endpoints (Projects "report" mode).
// A report is an editable LaTeX document authored from a project's sources and
// compiled to PDF server-side by Tectonic. All calls go through the shared `api`.
import { api, API_BASE_URL, API_PREFIX, ApiError } from "./client";
import { tokenStore } from "./token-store";

export type ReportStatus = "pending" | "running" | "complete" | "failed";
export type ReportCompileStatus =
  | "pending"
  | "compiling"
  | "success"
  | "error"
  | "timeout"
  | "unavailable";

export const REPORT_IN_FLIGHT: ReportStatus[] = ["pending", "running"];

/** One source file's agent-produced lookup index (structure + citable keys). */
export interface ReportFileSurvey {
  id: string;
  filename: string;
  structure: string[];
  keys: string[];
  summary: string;
}

export interface ReportOutlineSection {
  title: string;
  directives: string[];
  source_file_ids: string[];
}
export interface ReportOutline {
  doc_title?: string;
  abstract?: string;
  sections?: ReportOutlineSection[];
}

export interface ReportDocument {
  id: string;
  project_id: string;
  status: ReportStatus;
  compile_status: ReportCompileStatus;
  compile_log: string | null;
  latex_source: string | null;
  pdf_url: string | null;
  outline: ReportOutline | null;
  file_surveys: ReportFileSurvey[];
  assets: ReportAsset[];
  has_bib: boolean;
  solve_status: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

const enc = encodeURIComponent;

export const generateReport = (
  id: string,
  opts?: {
    language?: string;
    target_pages?: number;
    max_files?: number;
    mode?: "broad" | "focused";
    spine_file_id?: string;
    capture_figures?: boolean;
    caption_figures?: boolean;
    // Optional free-text brief steering the draft (angle/emphasis). Omit when empty.
    brief?: string;
  },
) => api.post<ReportDocument>(`/projects/${enc(id)}/report`, opts ?? {});

/** Cross-feed: generate a study set FROM this report (async; poll the project run).
 *  `label_requests` forces a "label the diagram" question on each referenced figure. */
export const generateStudySetFromReport = (
  id: string,
  opts?: { language?: string; label_requests?: { asset_id: string; instruction: string }[] },
) => api.post<unknown>(`/projects/${enc(id)}/report/generate-study-set`, opts ?? {});

export const getReport = (id: string) =>
  api.get<ReportDocument>(`/projects/${enc(id)}/report`);

export const saveReportLatex = (id: string, latexSource: string) =>
  api.patch<ReportDocument>(`/projects/${enc(id)}/report`, {
    latex_source: latexSource,
  });

export const recompileReport = (id: string) =>
  api.post<ReportDocument>(`/projects/${enc(id)}/report/compile`, {});

// ── iteration 2: assets, rewrite, bib, solve ─────────────────────────────────

export interface ReportAsset {
  id: string;
  filename: string;
  url: string | null;
  caption: string | null;
  tags: string[];
  origin: "upload" | "extracted" | "marked" | "plot" | "solve";
  source_file_id: string | null;
  source_page: number | null;
  inserted: boolean;
  created_at: string;
}

export interface FigureCaptureStatus {
  figure_capture_status: string | null;
}

/** A figure from the shared PROJECT pool (extracted once; reusable by the study
 * set and the report). `added` marks pool figures already in this report's
 * library; `folder_path` mirrors the upload hierarchy for a folder-tree view. */
export interface ReportPoolFigure {
  key: string;
  url: string | null;
  caption: string;
  source_file_id: string | null;
  source_filename: string;
  folder_path: string;
  source_page: number | null;
  origin: "extracted" | "plot" | "requested";
  added: boolean;
  asset_id: string | null;
}

export interface ReportAssetsList {
  assets: ReportAsset[];
  pool: ReportPoolFigure[];
}

export interface SectionTarget {
  section_index?: number;
  section_title?: string;
  char_start?: number;
  char_end?: number;
}

export interface RewriteResult {
  target_start: number;
  target_end: number;
  proposed_latex: string | null;
  rationale: string;
  citations_added: string[];
  document: ReportDocument | null;
}

export interface ReportBibEntry {
  key: string;
  title: string;
  authors: string;
  year: string;
}
export interface ReportBib {
  bib_source: string;
  entries: ReportBibEntry[];
}

export interface ReportSolveStatus {
  solve_status: string | null;
  solve_error: string | null;
}

export const rewriteSection = (
  id: string,
  body: SectionTarget & {
    directive: string;
    source_sha256?: string;
    source_file_ids?: string[];
    use_pdf_vision?: boolean;
    language?: string;
    preview?: boolean;
  },
) => api.post<RewriteResult>(`/projects/${enc(id)}/report/rewrite`, body);

export const listReportAssets = (id: string) =>
  api.get<ReportAssetsList>(`/projects/${enc(id)}/report/assets`);

/** Materialize a shared-pool figure into the report library by reference. */
export const addReportAssetFromPool = (
  id: string,
  body: { key: string; caption?: string },
) => api.post<ReportAsset>(`/projects/${enc(id)}/report/assets/from-pool`, body);

export const findReportFigure = (
  id: string,
  body: { query: string; source_file_ids?: string[]; do_cleanup?: boolean },
) => api.post<ReportAsset>(`/projects/${enc(id)}/report/assets/find`, body);

// Project-level figure library (iteration 3): bulk capture + local CLIP search.
// Coexists with the pool/find methods above — all feed/read the same library.
export const listProjectFigures = (
  id: string,
  params?: { source_file_id?: string; q?: string; limit?: number },
) =>
  api.get<{ figures: ReportAsset[] }>(`/projects/${enc(id)}/figures`, {
    query: {
      ...(params?.source_file_id ? { source_file_id: params.source_file_id } : {}),
      ...(params?.q ? { q: params.q } : {}),
      ...(params?.limit ? { limit: params.limit } : {}),
    },
  });

export const captureProjectFigures = (
  id: string,
  body?: { file_ids?: string[]; only_marked?: boolean; caption?: boolean },
) => api.post<FigureCaptureStatus>(`/projects/${enc(id)}/figures/capture`, body ?? {});

export const getFigureCaptureStatus = (id: string) =>
  api.get<FigureCaptureStatus>(`/projects/${enc(id)}/figures/capture`);

export const insertReportAsset = (
  id: string,
  assetId: string,
  body: { section_index?: number; char_position?: number; width?: string; caption_override?: string },
) => api.post<ReportDocument>(`/projects/${enc(id)}/report/assets/${enc(assetId)}/insert`, body);

export const deleteReportAsset = (id: string, assetId: string) =>
  api.delete<void>(`/projects/${enc(id)}/report/assets/${enc(assetId)}`);

export const getReportBib = (id: string) =>
  api.get<ReportBib>(`/projects/${enc(id)}/report/bib`);

export const extractReportBib = (id: string) =>
  api.post<ReportBib>(`/projects/${enc(id)}/report/bib/extract`, {});

export const putReportBib = (id: string, bibSource: string) =>
  api.put<ReportBib>(`/projects/${enc(id)}/report/bib`, { bib_source: bibSource });

export interface ReportBibCandidate {
  key: string;
  title: string;
  authors: string;
  year: string;
  venue: string;
  doi: string;
  source: string;
  bibtex: string;
}

/** Search Crossref / arXiv for references. Queries go to an external service. */
export const searchReportBib = (
  id: string,
  body: { query: string; source?: "all" | "crossref" | "arxiv"; limit?: number },
) => api.post<{ candidates: ReportBibCandidate[] }>(`/projects/${enc(id)}/report/bib/search`, body);

/** Incrementally survey project sources that have no lookup index yet. */
export const surveyReportSources = (
  id: string,
  body?: { language?: string; force?: boolean },
) => api.post<ReportDocument>(`/projects/${enc(id)}/report/sources/survey`, body ?? {});

export const solveReportExercise = (
  id: string,
  body?: { exercise_file_id?: string; language?: string },
) => api.post<ReportDocument>(`/projects/${enc(id)}/report/solve-exercise`, body ?? {});

export const getReportSolveStatus = (id: string) =>
  api.get<ReportSolveStatus>(`/projects/${enc(id)}/report/solve-exercise`);

/** Multipart upload of a user's own image into the report library. */
export async function uploadReportAsset(
  id: string,
  file: File,
  caption?: string,
): Promise<ReportAsset> {
  const form = new FormData();
  form.append("file", file, file.name);
  if (caption) form.append("caption", caption);
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = tokenStore.access;
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(
    `${API_BASE_URL}${API_PREFIX}/projects/${enc(id)}/report/assets`,
    { method: "POST", headers, body: form },
  );
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") message = data.detail;
    } catch {
      /* keep generic */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as ReportAsset;
}
